import { Alert, Card, Table } from 'antd';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LmsStatusTag from '@/pages/education/components/LmsStatusTag';
import { useLmsAccess } from '@/pages/education/utils/lmsAccess';

export type PortalHomework = {
  read_only?: true;
  advisory?: string;
  assignments: Array<{
    id: string;
    title: string;
    subject_name?: string;
    section_name?: string;
    due_on: string;
    submission_status: string;
    submitted_at?: string | null;
    has_feedback?: boolean;
  }>;
};

/** Read-only homework soft-read for student / parent portals. Status only — no payment actions. */
export default function PortalHomeworkCard({
  homework,
  studentId,
  loading,
}: {
  homework?: PortalHomework;
  /** Set for a granted guardian so the submit page targets the child. */
  studentId?: string;
  loading?: boolean;
}) {
  const { t } = useTranslation('education');
  const { canSubmit } = useLmsAccess();
  if (!homework) return null;
  const suffix = studentId ? `?student_id=${encodeURIComponent(studentId)}` : '';
  return (
    <Card size="small" title={t('lmsPortalHomework', { defaultValue: 'Homework' })} loading={loading}>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message={t('lmsPortalHomeworkHint', { defaultValue: 'Published homework for the section.' })}
      />
      <Table
        rowKey="id"
        size="small"
        pagination={false}
        locale={{ emptyText: t('lmsNoRows', { defaultValue: 'Nothing here yet' }) }}
        dataSource={homework.assignments}
        columns={[
          { title: t('title', { defaultValue: 'Title' }), dataIndex: 'title' },
          { title: t('subject', { defaultValue: 'Subject' }), dataIndex: 'subject_name' },
          { title: t('lmsDueOn', { defaultValue: 'Due on' }), dataIndex: 'due_on', width: 110 },
          {
            title: t('status', { defaultValue: 'Status' }),
            dataIndex: 'submission_status',
            width: 140,
            render: (v: string) => <LmsStatusTag status={v} />,
          },
          {
            title: '',
            width: 90,
            render: (_: unknown, r: PortalHomework['assignments'][number]) =>
              canSubmit ? (
                <Link to={`/education/lms/homework/${r.id}/submit${suffix}`}>{t('open', { defaultValue: 'Open' })}</Link>
              ) : null,
          },
        ]}
      />
    </Card>
  );
}
